// i18n deferred: admin/blog/legal pages are intentionally excluded from Step 2 migration.
import { useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useBlogPost, useBlogPosts } from "../hooks/useBlog";
import { LandingHeader } from "@/components/layout/LandingHeader";
import { BlogPostArticle, BlogPostMissing } from "../components/blog";
import { ContentWithSidebarTemplate } from "../components/blog/article/article-layout";
import { Card, IconWrapper, Typography } from "@/components/ui/atoms";
import { Skeleton } from "@/components/ui/atoms/Skeleton";
import { Button } from "@/components/ui/atoms/Button";
import { AlertCircleIcon } from "lucide-react";

const RELATED_ARTICLE_SKELETON_COUNT = 3;
const RELATED_ARTICLE_SKELETON_HEIGHT_CLASS = "h-14";

export function BlogPostPage() {
  const { slug = "" } = useParams();
  const { data: post, isLoading, isError, error, refetch } = useBlogPost(slug);
  const { data: listData } = useBlogPosts(1, 8);

  return (
    <Layout hideHeader hideFooter fullPage className="min-h-screen bg-white">
      <LandingHeader />

      {isLoading ? (
        <div role="status" aria-label="Loading article" aria-busy={true}>
          <ContentWithSidebarTemplate
            content={
              <article className="mx-auto w-full max-w-[860px] overflow-hidden rounded-2xl">
                <header className="space-y-4 px-5 py-4 md:px-8">
                  <Skeleton decorative className="h-3 w-28" />
                  <Skeleton decorative className="h-8 w-full md:h-10" />
                  <Skeleton decorative className="h-8 w-[72%] md:h-10" />
                  <Skeleton.Text decorative lines={2} />
                  <div className="flex flex-col gap-4 border-y border-dark-invirtus-soft py-4 md:flex-row md:items-center md:justify-between">
                    <Skeleton decorative className="h-8 w-48" />
                    <div className="flex flex-wrap gap-2">
                      <Skeleton decorative className="h-6 w-20 rounded-full" />
                      <Skeleton decorative className="h-6 w-24 rounded-full" />
                      <Skeleton decorative className="h-6 w-28 rounded-full" />
                    </div>
                  </div>
                </header>

                <div className="px-5 py-8 md:px-8">
                  <div className="mx-auto max-w-[70ch] space-y-8">
                    <Skeleton.Text decorative lines={4} />
                    <Skeleton decorative className="h-5 w-2/5" />
                    <Skeleton.Text decorative lines={3} />
                    <Skeleton decorative className="h-5 w-1/3" />
                    <Skeleton.Text decorative lines={4} />
                  </div>
                </div>

                <div className="border-t border-dark-invirtus-soft px-5 py-8 md:px-8">
                  <div className="mx-auto max-w-[70ch] space-y-4">
                    <Skeleton decorative className="h-6 w-48" />
                    <div className="space-y-3">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton key={index} decorative className="h-12 w-full" />
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            }
            sidebar={
              <>
                <div className="hidden xl:block">
                  <Card variant="elevated">
                    <Skeleton decorative className="h-4 w-20" />
                    <Skeleton.Text decorative lines={6} className="mt-3" />
                  </Card>
                </div>
                <Card variant="elevated">
                  <Skeleton decorative className="h-4 w-28" />
                  <div className="mt-3 space-y-3">
                    {Array.from({ length: RELATED_ARTICLE_SKELETON_COUNT }).map((_, index) => (
                      <Skeleton
                        key={index}
                        decorative
                        className={`w-full ${RELATED_ARTICLE_SKELETON_HEIGHT_CLASS}`}
                      />
                    ))}
                  </div>
                </Card>
              </>
            }
          />
        </div>
      ) : null}

      {isError ? (
        <div className="mx-auto w-full max-w-7xl px-4 pt-4 sm:px-6">
          <div className="flex min-h-[80vh] items-center justify-center">
            <div className="mx-auto max-w-lg text-center">
              <IconWrapper size="xl" color="error">
                <AlertCircleIcon />
              </IconWrapper>
              <Typography as="p" variant="h5" color="primary" className="mt-4">
                Oops! Something went wrong.
              </Typography>
              <Typography as="p" variant="body-md" color="muted" className="mt-2">
                {error?.message || "We couldn’t load this post right now. Please try again."}
              </Typography>
              <div className="mt-5">
                <Button
                  type="outlined"
                  intent="secondary"
                  size="sm"
                  label="Try again"
                  onClick={() => void refetch()}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!isLoading && !isError && !post ? (
        <div className="mx-auto w-full max-w-7xl px-4 pt-4 sm:px-6">
          <BlogPostMissing />
        </div>
      ) : null}

      {!isLoading && !isError && post ? (
        <BlogPostArticle post={post} relatedPosts={listData?.items || []} />
      ) : null}
    </Layout>
  );
}

export default BlogPostPage;
